import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "p-4 sm:p-6 sm:pt-4 lg:p-8 lg:pt-4" }
const _hoisted_2 = { class: "mb-5 text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "flex flex-col justify-end lg:flex-row" }
const _hoisted_4 = { class: "w-full md:mt-3 lg:mt-0 lg:w-64" }
const _hoisted_5 = { class: "mt-5 w-full" }
const _hoisted_6 = { class: "flex h-14 rounded bg-gray-50 ring-1 ring-gray-300 focus:ring-2 focus:ring-opacity-50" }
const _hoisted_7 = { class: "flex flex-1 justify-between px-4" }
const _hoisted_8 = {
  class: "flex flex-1",
  action: "#",
  method: "GET"
}
const _hoisted_9 = { class: "ml-2 flex items-center space-x-4 sm:ml-6 sm:space-x-6" }
const _hoisted_10 = {
  key: 0,
  class: "flex gap-3 rounded border-t border-gray-200 bg-gray-50 p-2 ring-1 ring-gray-300 focus:ring-2 focus:ring-opacity-50"
}
const _hoisted_11 = { role: "list" }
const _hoisted_12 = {
  key: 0,
  class: "mt-0.5 flex h-48 flex-col flex-wrap items-center justify-center rounded-b bg-gray-200 font-semibold text-gray-600"
}

import { ref, computed, watch, ComputedRef, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import MeetingService from '@/services/api/MeetingService';
import { toastServiceError } from '@/services/core/notification';

import {
    FilterItem,
    LevelModel,
    MeetingModel,
    SortMenuItem,
    MeetingActions,
    MeetingGetters,
} from '@/types';

import {
    MeetingListItem,
    MeetingListSort,
    MeetingListFilters,
} from '@/views/pages/meeting/components';

// Stores & Routes
const perPage = 20;

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'MeetingPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const filterStore = useFilterStore();

// Constants
const meetings = ref<MeetingModel[]>([]);
const count = ref(0);

// Computed
const currentPage = computed<number>({
    get: () => store.getters[MeetingGetters.PAGE],
    set: (page: number) => store.dispatch(MeetingActions.SET_PAGE, page),
});

const search = computed<string>({
    get: () => store.getters[MeetingGetters.SEARCH],
    set: (search: string) => store.dispatch(MeetingActions.SET_SEARCH, search),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[MeetingGetters.SORT],
    set: (sort: SortMenuItem[]) => store.dispatch(MeetingActions.SET_SORT, sort),
});

const orderBy = computed<string>(() =>
    sortMenuSelected.value.map((s) => s.direction + s.key).join(','),
);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);

const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (dateRange: Array<Date>) => filterStore.setDateRange(dateRange),
});

const platform = computed<string | null>(() => {
    const platformFilter = store.getters[MeetingGetters.FILTER].platform;
    return platformFilter?.value?.toString() ?? null;
});

const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[MeetingGetters.ACTIVE_FILTERS],
);

// Methods
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const handleRemoveSortClick = (item: SortMenuItem) => {
    sortMenuSelected.value.splice(sortMenuSelected.value.indexOf(item), 1);
};

const handleRemoveFilter = (field: string) => {
    store.dispatch(MeetingActions.CLEAR_FILTER, field);
};

const prepareMeetingParams = () => {
    return {
        search: search.value,
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
        platform: platform.value,
        page: currentPage.value,
        per_page: perPage,
        order_by: orderBy.value,
    };
};

const getMeetings = async () => {
    const params = prepareMeetingParams();
    if (!params) return;

    try {
        await setLoading(true);
        const response = await MeetingService.getAll({ params });
        meetings.value = response.data;
        count.value = parseInt(response.headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

// Watchers
watch(
    [search, levelFilter, dateRange, orderBy, currentPage, activeFilters],
    () => {
        getMeetings();
    },
    { immediate: true },
);

// Lifecycle
onMounted(() => {
    if (route.query.clearStateOnSetup) {
        currentPage.value = 1;
        router.replace({ query: {} });
    }

    getMeetings();
});

return (_ctx: any,_cache: any) => {
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiSortMenuBadge = _resolveComponent("UiSortMenuBadge")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('dashboard.meetings')), 1)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: ""
        }, null, 8, ["modelValue"])
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("form", _hoisted_8, [
            _createVNode(_component_UiListSearch, {
              modelValue: search.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event))
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_unref(MeetingListSort), {
              modelValue: sortMenuSelected.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((sortMenuSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_unref(MeetingListFilters))
          ])
        ])
      ]),
      (
                    (sortMenuSelected.value && sortMenuSelected.value.length) ||
                    (activeFilters.value && activeFilters.value.length)
                )
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortMenuSelected.value, (selected) => {
              return (_openBlock(), _createBlock(_component_UiSortMenuBadge, {
                key: selected.key,
                item: selected,
                onRemove: ($event: any) => (handleRemoveSortClick(selected))
              }, null, 8, ["item", "onRemove"]))
            }), 128)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
              return (_openBlock(), _createBlock(_component_UiBadge, {
                key: filter,
                removable: "",
                onRemove: ($event: any) => (handleRemoveFilter(filter.field))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(`meeting.filters.${filter.field}.label`)) + ": " + _toDisplayString(filter.label), 1)
                ]),
                _: 2
              }, 1032, ["onRemove"]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("ul", _hoisted_11, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(meetings.value, (meeting, index) => {
          return (_openBlock(), _createBlock(_unref(MeetingListItem), {
            key: index,
            "model-value": meeting
          }, null, 8, ["model-value"]))
        }), 128)),
        (!meetings.value.length)
          ? (_openBlock(), _createElementBlock("li", _hoisted_12, _toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1))
          : _createCommentVNode("", true)
      ]),
      (meetings.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 1,
            modelValue: currentPage.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((currentPage).value = $event)),
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})